import React, { useState, useEffect } from 'react';
import businessService, { Business } from '../../services/businessService';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BusinessForm from './BusinessForm';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const BusinessList: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [rubrosDisponibles, setRubrosDisponibles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });

  // Filtros
  const [search, setSearch] = useState('');
  const [rubro, setRubro] = useState('');
  const [activo, setActivo] = useState('true'); // Por defecto mostrar solo activos

  // Modales
  const [openForm, setOpenForm] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<Business | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Cargar negocios
  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search) params.search = search;
      if (rubro) params.industry = rubro;
      if (activo !== '') params.active = activo;

      const response = await businessService.list(params);

      setBusinesses(response.data || []);
      setPagination({
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 10,
        pages: response.pagination?.pages || 0
      });
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
      setError(err.response?.data?.error || 'Error al cargar negocios');
    } finally {
      setLoading(false);
    }
  };

  // Cargar rubros únicos disponibles
  const fetchRubros = async () => {
    try {
      const response = await businessService.list({ limit: 1000 });
      const uniqueRubros = [...new Set(
        (response.data || [])
          .map((b: Business) => b.industry)
          .filter(Boolean)
      )].sort() as string[];
      setRubrosDisponibles(uniqueRubros);
    } catch (err) {
      console.error('Error al cargar rubros:', err);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [pagination.page, pagination.limit, search, rubro, activo]);

  useEffect(() => {
    fetchRubros();
  }, []);

  // Handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 1 }));
  };

  const handleOpenCreate = () => {
    setSelectedBusiness(null);
    setOpenForm(true);
  };

  const handleOpenEdit = (business: Business) => {
    setSelectedBusiness(business);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedBusiness(null);
  };

  const handleSaveSuccess = () => {
    handleCloseForm();
    fetchBusinesses();
  };

  const handleOpenDelete = (business: Business) => {
    setBusinessToDelete(business);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteDialog(false);
    setBusinessToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!businessToDelete) return;

    try {
      setDeleting(true);
      console.log('Eliminando negocio:', businessToDelete.id);
      
      await businessService.delete(businessToDelete.id);
      
      console.log('Negocio eliminado exitosamente');
      
      // Cerrar el diálogo
      handleCloseDelete();
      
      // Actualizar la lista: eliminar el negocio del estado local
      setBusinesses(prev => prev.filter(b => b.id !== businessToDelete.id));
      
      // Actualizar el total en la paginación
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      
    } catch (err: any) {
      console.error('Error al eliminar negocio:', err);
      setError(err.response?.data?.error || 'Error al eliminar negocio');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (business: Business) => {
    try {
      const response = await businessService.toggleStatus(business.id);
      
      // Actualizar el negocio en la lista local
      setBusinesses(prev => 
        prev.map(b => 
          b.id === business.id 
            ? { ...b, active: response.active }
            : b
        )
      );
      
      // Mostrar mensaje de éxito
      setError(null);
      
    } catch (err: any) {
      console.error('Error al cambiar estado del negocio:', err);
      setError(err.response?.data?.error || 'Error al cambiar estado del negocio');
    }
  };

  const handleViewDetail = (business: Business) => {
    navigate(`/business/${business.id}`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch('');
    setRubro('');
    setActivo('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getScoreColor = (nota: number | null) => {
    if (!nota) return 'default';
    if (nota >= 80) return 'success';
    if (nota >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.25rem', sm: '2.125rem' },
          flexShrink: 1,
          minWidth: 0,
        }}>
          Gestión de Negocios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={handleOpenCreate}
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            py: { xs: 0.75, sm: 1.5 },
            px: { xs: 1.5, sm: 3 },
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Nuevo Negocio
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Buscar por nombre o rubro..."
            value={search}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          <Autocomplete
            size="small"
            options={rubrosDisponibles}
            value={rubro || null}
            onChange={(_event, newValue) => {
              setRubro(newValue || '');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rubro"
                placeholder="Todos los rubros"
              />
            )}
            ListboxProps={{
              sx: {
                maxHeight: 300,
                '& .MuiAutocomplete-option': {
                  padding: '8px 16px',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: 'action.selected',
                  },
                },
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="estado-filter-label" htmlFor="estado-filter-input">Estado</InputLabel>
            <Select
              labelId="estado-filter-label"
              id="estado-filter"
              value={activo}
              label="Estado"
              onChange={(e) => {
                setActivo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              inputProps={{
                name: 'estado',
                id: 'estado-filter-input',
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Activos</MenuItem>
              <MenuItem value="false">Inactivos</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleClearFilters}
            sx={{ height: 40 }}
          >
            Limpiar
          </Button>
        </Box>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Rubro</strong></TableCell>
              <TableCell><strong>Contacto</strong></TableCell>
              <TableCell align="center"><strong>Auditorías</strong></TableCell>
              <TableCell align="center"><strong>Promedio</strong></TableCell>
              <TableCell align="center"><strong>Última Nota</strong></TableCell>
              <TableCell align="center"><strong>Usuarios</strong></TableCell>
              <TableCell align="center"><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : businesses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    No se encontraron negocios
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              businesses.map((business) => (
                <TableRow key={business.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {business.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={business.industry || 'Sin rubro'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{business.manager_name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {business.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={business.total_audits} color="primary" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {business.average_score !== null && business.average_score !== undefined ? (
                      <Chip
                        label={Number(business.average_score).toFixed(1)}
                        color={getScoreColor(Number(business.average_score))}
                        size="small"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {business.last_score !== null && business.last_score !== undefined ? (
                      <Chip
                        label={Number(business.last_score).toFixed(1)}
                        color={getScoreColor(Number(business.last_score))}
                        size="small"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={business.assigned_users} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={business.active ? 'Activo' : 'Inactivo'}
                      color={business.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => handleViewDetail(business)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(business)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {business.active ? (
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleOpenDelete(business)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivar">
                        <IconButton size="small" color="success" onClick={() => handleToggleStatus(business)}>
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          SelectProps={{
            inputProps: {
              name: 'rows-per-page',
              id: 'pagination-rows-per-page'
            }
          }}
        />
      </TableContainer>

      {/* Modal de Formulario */}
      <BusinessForm
        open={openForm}
        business={selectedBusiness}
        onClose={handleCloseForm}
        onSuccess={handleSaveSuccess}
      />

      {/* Modal de Confirmación de Eliminación */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        title="Eliminar Negocio"
        message="¿Estás seguro de que deseas eliminar este negocio? Esta acción no se puede deshacer."
        itemName={businessToDelete?.name}
        onCancel={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </Box>
  );
};

export default BusinessList;
